import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonDirective } from 'primeng/button';
import type { ButtonSeverity } from 'primeng/types/button';

export type AppButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'soft';
export type AppButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type AppButtonType = 'button' | 'submit' | 'reset';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, RouterLink, ButtonDirective],
  templateUrl: './button.component.html',
})
export class AppButtonComponent {
  readonly variant = input<AppButtonVariant>('primary');
  readonly size = input<AppButtonSize>('md');
  readonly type = input<AppButtonType>('button');
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly fullWidth = input(false);
  readonly className = input('');
  readonly ariaLabel = input<string | null>(null);
  /**
   * Test hook, forwarded to the rendered <button>/<a> rather than the host element, so
   * Playwright assertions like toBeDisabled() read the control itself.
   */
  readonly testId = input<string | null>(null);
  readonly title = input<string | null>(null);
  readonly href = input<string | null>(null);
  readonly target = input<string | null>(null);
  readonly rel = input<string | null>(null);
  readonly routerLink = input<string | any[] | null>(null);

  readonly pressed = output<MouseEvent>();

  readonly isDisabled = computed(() => this.disabled() || this.loading());

  readonly effectiveRel = computed(() => {
    if (this.rel()) return this.rel();
    return this.target() === '_blank' ? 'noopener noreferrer' : null;
  });

  /** danger keeps PrimeNG's own danger severity — semantically "destructive", independent of the brand/primary color. */
  readonly severity = computed<ButtonSeverity>(() => {
    switch (this.variant()) {
      case 'secondary':
      case 'ghost':
        return 'secondary';
      case 'danger':
        return 'danger';
      default:
        return undefined;
    }
  });

  readonly isText = computed(() => this.variant() === 'ghost');
  readonly isOutlined = computed(() => this.variant() === 'soft');

  readonly pSize = computed<'small' | 'large' | undefined>(() => {
    switch (this.size()) {
      case 'xs':
      case 'sm':
        return 'small';
      case 'lg':
        return 'large';
      default:
        return undefined;
    }
  });

  readonly classes = computed(() =>
    [this.size() === 'xs' ? 'text-xs px-2.5 py-1.5' : '', this.className()].filter(Boolean).join(' '),
  );

  onClick(event: MouseEvent): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.pressed.emit(event);
  }
}
